import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Segmented } from 'antd';
import { PackagePlus, Pencil, Plus, Trash2 } from 'lucide-react';

import { ROUTE_URLS } from '../../routes';
import { createLink, formatNumber, searchFn } from '../../utils';
import { useProducts, useBranches, useDeleteProduct } from '../../hooks';
import {
  DataTable,
  DeleteConfirmModal,
  getStockStatus,
  PageHeader,
  RowActions,
  SearchInput,
  StockBadge,
} from '../../components';
import { useI18n } from '../../i18n';

export const Products = () => {
  const { t } = useI18n();
  const { data: products, isLoading, isError, refetch } = useProducts();
  const { data: branches, isLoading: isLoadingBranches } = useBranches();
  const { mutateAsync: deleteProduct } = useDeleteProduct();

  const [searchText, setSearchText] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const findBranchPriceForProduct = useCallback((branchId, branchPrices) => {
    return branchPrices.find(el => el.id === branchId)?.sell_price || 0;
  }, []);

  const generatedColumns = useMemo(() => {
    if (!Array.isArray(branches)) return [];
    return branches.map(el => ({
      title: el.name,
      key: `branch-${el.id}`,
      dataIndex: 'id',
      align: 'right',
      width: 150,
      render: (text, product) => (
        <span className='num'>{formatNumber(findBranchPriceForProduct(el.id, product.branches))}</span>
      ),
      sorter: (a, b) =>
        findBranchPriceForProduct(el.id, a.branches) - findBranchPriceForProduct(el.id, b.branches),
    }));
  }, [branches, findBranchPriceForProduct]);

  const columns = useMemo(
    () => [
      {
        title: t('products.name'),
        dataIndex: 'name',
        fixed: 'left',
        width: 240,
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (text, product) => (
          <div className='wm-cell-main'>
            <span className='wm-cell-icon'>{product.name.slice(0, 2)}</span>
            <Link to={createLink(ROUTE_URLS.EDIT_PRODUCT, product.id)} className='wm-link-strong'>
              {product.name}
            </Link>
          </div>
        ),
      },
      {
        title: t('products.stock'),
        dataIndex: 'quantity',
        sorter: (a, b) => a.quantity - b.quantity,
        width: 190,
        render: quantity => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span className='wm-strong num' style={{ minWidth: 40 }}>
              {formatNumber(quantity)}
            </span>
            <StockBadge quantity={quantity} />
          </span>
        ),
      },
      {
        title: t('products.buyPrice'),
        dataIndex: 'buy_price',
        align: 'right',
        render: price => <span className='num'>{formatNumber(price)}</span>,
        sorter: (a, b) => a.buy_price - b.buy_price,
        width: 170,
      },
      ...(generatedColumns.length
        ? [{ title: t('products.branchPrices'), key: 'branches', children: generatedColumns }]
        : []),
      {
        title: '',
        key: 'actions',
        dataIndex: 'id',
        fixed: 'right',
        width: 64,
        align: 'center',
        render: (text, product) => (
          <RowActions
            items={[
              {
                key: 'edit',
                icon: Pencil,
                label: t('common.edit'),
                to: createLink(ROUTE_URLS.EDIT_PRODUCT, product.id),
              },
              { type: 'divider', key: 'd' },
              {
                key: 'delete',
                icon: Trash2,
                danger: true,
                label: t('common.delete'),
                onClick: () => {
                  setSelectedProductId(product.id);
                  setShowDeleteModal(true);
                },
              },
            ]}
          />
        ),
      },
    ],
    [generatedColumns, t],
  );

  const lowCount = useMemo(
    () => (Array.isArray(products) ? products.filter(p => getStockStatus(p.quantity) !== 'ok').length : 0),
    [products],
  );

  const filteredProducts = useMemo(() => {
    const list = searchFn(Array.isArray(products) ? products : [], searchText);
    return stockFilter === 'low' ? list.filter(p => getStockStatus(p.quantity) !== 'ok') : list;
  }, [products, searchText, stockFilter]);

  const confirmHandler = () => {
    deleteProduct(selectedProductId);
    resetState();
  };

  const resetState = () => {
    setShowDeleteModal(false);
    setSelectedProductId(null);
  };

  return (
    <>
      <DeleteConfirmModal open={showDeleteModal} onConfirm={confirmHandler} onCancel={resetState} />
      <PageHeader
        title={t('products.title')}
        subtitle={t('products.subtitle')}
        actions={
          <>
            <Link to={ROUTE_URLS.ADD_PRODUCT}>
              <Button icon={<PackagePlus size={16} />}>{t('products.addStock')}</Button>
            </Link>
            <Link to={ROUTE_URLS.CREATE_PRODUCT}>
              <Button type='primary' icon={<Plus size={16} />}>
                {t('products.create')}
              </Button>
            </Link>
          </>
        }
      />
      <div className='wm-toolbar'>
        <SearchInput value={searchText} onChange={setSearchText} placeholder={t('products.searchPlaceholder')} />
        <Segmented
          value={stockFilter}
          onChange={setStockFilter}
          options={[
            { value: 'all', label: t('products.filterAll') },
            {
              value: 'low',
              label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {t('products.filterLow')}
                  {lowCount > 0 && <span className='wm-badge wm-badge--warning wm-badge--plain'>{lowCount}</span>}
                </span>
              ),
            },
          ]}
        />
        <span className='wm-toolbar__grow' />
        {Array.isArray(products) && (
          <span className='wm-badge wm-badge--plain'>{t('products.count', { count: products.length })}</span>
        )}
      </div>
      <DataTable
        dataSource={filteredProducts}
        columns={columns}
        isLoading={isLoading || isLoadingBranches}
        isError={isError}
        onRetry={refetch}
        bordered={generatedColumns.length > 0}
        emptyVariant={searchText || stockFilter !== 'all' ? 'search' : undefined}
      />
    </>
  );
};
